import { useCallback, useEffect, useMemo } from 'react';

import { CommandConfirmedPayloads, CommandLifecycle } from '@coinweb/webapp-library';

import { useQrState } from './useQrState';
import { useWalletConnectCore } from './useWalletConnectCore';
import { useWalletLifecycleMessages } from './useWalletLifecycleMessages';

import { useUserPubKey } from '.';

export const useCoinwebWalletConnect = () => {
  const { pubKey: userWalletPubKey, updatePubKey } = useUserPubKey();

  const { qrJson, setQrJson, debounceSetQrJson } = useQrState();
  const { message, generateMessage, clearMessage } = useWalletLifecycleMessages();
  const { connect: coreConnect, cancelPendingRequest, disconnect, isConnecting } = useWalletConnectCore();

  // cancel pending request when QR closes (matches previous behavior)
  useEffect(() => {
    if (!qrJson) {
      void cancelPendingRequest();
      clearMessage();
    }
  }, [qrJson, cancelPendingRequest, clearMessage]);

  const connect = useCallback((): Promise<void> => {
    // Create a promise that resolves when lifecycle events complete
    return new Promise((resolve, reject) => {
      const qr = coreConnect({
        onLifecycle: (lifecycle, payload, ctx) => {
          generateMessage(lifecycle, ctx.id);

          switch (lifecycle) {
            case CommandLifecycle.Confirmed: {
              const walletPubKey = (payload as CommandConfirmedPayloads['wallet.connect'])?.result.walletPubKey;
              if (walletPubKey) {
                if (!userWalletPubKey) {
                  updatePubKey(walletPubKey);
                }
                debounceSetQrJson(null);
                resolve();
              } else {
                debounceSetQrJson(null);
                reject(new Error('No wallet public key received'));
              }
              break;
            }
            case CommandLifecycle.Cancelled:
              debounceSetQrJson(null);
              reject(new Error('Connection cancelled by user'));
              break;
            case CommandLifecycle.Failed:
              debounceSetQrJson(null);
              reject(new Error('Connection failed'));
              break;
          }
        },
        onLegacyData: (data) => {
          if (data.user_pubkey) {
            if (!userWalletPubKey) {
              updatePubKey(data.user_pubkey);
            }
            debounceSetQrJson(null);
            resolve();
          } else {
            debounceSetQrJson(null);
            reject(new Error('No wallet public key received'));
          }
        },
        onError: (error) => {
          debounceSetQrJson(null);
          reject(error);
        },
      });

      if (qr) {
        setQrJson(qr);
      } else {
        reject(new Error('Failed to generate QR code'));
      }
    });
  }, [generateMessage, debounceSetQrJson, coreConnect, setQrJson, updatePubKey, userWalletPubKey]);

  const hardDisconnect = useCallback(() => {
    disconnect();
    updatePubKey(null);
    setQrJson(null);
    clearMessage();
  }, [clearMessage, disconnect, setQrJson, updatePubKey]);

  const value = useMemo(
    () => ({
      address: userWalletPubKey,
      isConnected: !!userWalletPubKey,
      connect,
      disconnect: hardDisconnect,
      isConnecting,
      qrJson,
      message,
      setQrJson, // consumers can close the dialog
    }),
    [connect, hardDisconnect, isConnecting, message, qrJson, setQrJson, userWalletPubKey]
  );

  return value;
};
